import router, { resetRouter } from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import { useCache } from '@/hooks/web/useCache'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { getQueryObject } from '@/utils'
import { getRouters } from '@/api/router'
import { getUserInfo } from '@/api/login'
import { usePermissionListStoreWithOut } from '@/store/modules/permissionList'
import {getPermission} from '@/api/premiss'
import { useTagsViewStoreWithOut } from '@/store/modules/tagsView'
import {
  PRIMARY_ACCESS_TOKEN_KEY,
  VISIT_DATA_TOKEN_KEY,
  clearPrimaryAccessContext,
  isVisitToken,
  mergeCompanyPlatform,
  rememberPrimaryAccessToken,
  rememberPrimaryCompany,
  restorePrimaryAccessContext,
  setVisitDataContext
} from '@/utils/accessContext'

import axios from 'axios'
import { config } from './config/axios/config'
const { result_code, base_url } = config


const permissionListStore = usePermissionListStoreWithOut()

const permissionStore = usePermissionStoreWithOut()
const tagsViewStore = useTagsViewStoreWithOut()
const appStore = useAppStoreWithOut()
const { wsCache } = useCache()
const { start, done } = useNProgress()
const { loadStart, loadDone } = usePageLoading()
type QueryToken = {
  token?: string
}

type VisitDataRouteContext = {
  token: string
  platform: string
}

const safeDecodeRouteParam = (value = '') => {
  try {
    return decodeURIComponent(value)
  } catch (_error) {
    return value
  }
}

const getVisitDataRouteContext = (path: string): VisitDataRouteContext | null => {
  if (!path.startsWith('/premiss/UserDatas/')) return null
  const [, , , _id, token, platform] = path.split('/')
  if (!token) return null

  return {
    token: safeDecodeRouteParam(token),
    platform: safeDecodeRouteParam(platform || '')
  }
}

const applyVisitDataRouteContext = (context: VisitDataRouteContext | null): boolean => {
  if (!context?.token) return false
  const primaryToken = wsCache.get(PRIMARY_ACCESS_TOKEN_KEY)
  const shouldKeepPrimaryToken = primaryToken && primaryToken !== context.token

  if (shouldKeepPrimaryToken) {
    restorePrimaryAccessContext(false)
    return setVisitDataContext(context.token, context.platform)
  }

  return setVisitDataContext(context.token, context.platform, undefined, true)
}

const fetchCompanyConfig = async (token: string) => {
  if (!token) return null
  const PATH_URL = base_url[import.meta.env.VITE_API_BASEPATH]
  return axios({
    method:'post',
    url:PATH_URL+'admin/config/company',
    data:{
      token
    },
    headers:{
    }
  }).then(res => {
    if (res?.data?.code === result_code) {
      wsCache.set('company', res)
      if (!isVisitToken(token)) {
        rememberPrimaryCompany(res)
      }
      return res
    }
    return null
  }).catch(() => null)
}

const refreshCurrentUserInfo = async () => {
  const res = await getUserInfo()
  wsCache.set(appStore.userInfo, res.data.userinfo)
  permissionListStore.filterPermissionList()
}

const resetDynamicRoutesForTokenChange = () => {
  resetRouter()
  permissionStore.resetRoutes()
  tagsViewStore.delAllViews()
  wsCache.delete('routers')
  wsCache.delete('permissionList')
  localStorage.removeItem('requestUserInfo')
}

const applyQueryTokenContext = async (token?: string): Promise<boolean> => {
  if (!token) return false

  const queryTokenChanged = wsCache.get('access_token') !== token
  wsCache.set('access_token', token)

  if (isVisitToken(token)) {
    clearPrimaryAccessContext()
  } else {
    rememberPrimaryAccessToken(token)
  }

  if (queryTokenChanged) {
    resetDynamicRoutesForTokenChange()
    wsCache.delete(VISIT_DATA_TOKEN_KEY)
  }

  await fetchCompanyConfig(token)
  return queryTokenChanged
}
// const whiteList = [] // 不重定向白名单
// 缓存页面
const cacheRouteList: string[] = [
  '收款管理',
  '获客线索',
  '客户管理',
  '客户公海',
  '目标管理',
  '合同订单',
  '分单记录',
  '收款管理',
  '退货退款',
  '组织架构',
  '消息推送模版',
  '用户管理',
  '获客线索导入记录',
  '获客线索导入详情',
  '销售排货单'
]

function updateRouteName(routes: any[], titles: string[], parentPath = ''): void {
  for (const route of routes) {
    if (route.meta.title && titles.includes(route.meta.title)) {
      if (!route.path) {
        route.name = parentPath.replace(/\//g, '').replace(/^\w/, (c) => c.toUpperCase())
      } else {
        route.name = route.path.replace(/\//g, '').replace(/^\w/, (c) => c.toUpperCase())
      }
    }
    // 递归处理子路由
    if (route.children) {
      updateRouteName(route.children, titles, route.path)
    }
  }
}
const getRouter = async () => {
    // 调用获取权限接口
    const userId = wsCache.get('userInfo')?.id
    if (userId) {
      await getPermission({ userId }).then(res => {
        wsCache.set('permissionList', res.data || [])
      }).catch(() => {
        wsCache.set('permissionList', [])
      })
    }

  const res = await getRouters()
  updateRouteName(res.data.list!, cacheRouteList)
  const routers = res.data.list || []

  if(routers && routers.length>0){
    for(let i=0;i<routers.length;i++){
      const fchillren = routers[i].children;
      if(fchillren && fchillren.length>0){
        for(let k=0;k<fchillren.length;k++){
          // approvalPurchaseManagement
          // console.log(fchillren[k].name);
          if(fchillren[k].name=='审核/采购管理'){
            fchillren[k].name = 'ApprovalPurchaseManagement';
          }
          // 合同订单
          if(fchillren[k].name=='ContractManagementorderContract'){
            fchillren[k].name = 'OrderContract';
          }
          // 收款单
          if(fchillren[k].name=='收款单'){
            fchillren[k].name = 'CollectionManagement';
          }
          // 退货退款
          if(fchillren[k].name=='FinanceManagementrefundProduct'){
            fchillren[k].name = 'RefundProduct';
          }
          // 分单记录
          if(fchillren[k].name=='ContractManagementsplitRecords'){
            fchillren[k].name = 'SplitRecords';
          }
          // 组织管理
          if(fchillren[k].name=='组织管理'){
            fchillren[k].name = 'TissueManage';
          }
          // 送货单
          if(fchillren[k].name=='送货单'){
            fchillren[k].name = 'DeliveryNote';
          }
          // 送货单
          if(fchillren[k].name=='退货单'){
            fchillren[k].name = 'ReturnOrder';
          }
        }
      }
    }
  }

  wsCache.set('routers', routers)
  await permissionStore
    .generateRoutes('admin', routers as AppCustomRouteRecordRaw[])
    .catch(() => {})
  permissionStore.getAddRouters.forEach((route) => {
    router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
  })
  permissionStore.setIsAddRouters(true)
}
router.beforeEach(async (to, from, next) => {
  start()
  loadStart()
  const href: string = window.location.href
  const token: QueryToken = getQueryObject(href)
  const queryToken = token.token || ''
  const visitDataContext = getVisitDataRouteContext(to.path)
  const primaryToken = wsCache.get(PRIMARY_ACCESS_TOKEN_KEY)
  const shouldUsePrimaryForVisitData = Boolean(
    visitDataContext?.token && primaryToken && primaryToken !== visitDataContext.token
  )
  const primaryTokenRestored = !queryToken && !visitDataContext && restorePrimaryAccessContext()
  const visitDataTokenChanged = applyVisitDataRouteContext(visitDataContext)
  if (primaryTokenRestored || (visitDataTokenChanged && !shouldUsePrimaryForVisitData)) {
    resetDynamicRoutesForTokenChange()
  }

  if (wsCache.get(appStore.getUserInfo)) {
    if(queryToken){
      const queryTokenChanged = await applyQueryTokenContext(queryToken)

      // 处理 已经登陆过 切换token 只调用一次 获取用户信息 接口
      if(queryTokenChanged || !localStorage.getItem('requestUserInfo')){
        await refreshCurrentUserInfo()
        localStorage.setItem('requestUserInfo', '1');
      }else{
        localStorage.removeItem('requestUserInfo');
      }
    }
    if (visitDataContext && visitDataTokenChanged && !shouldUsePrimaryForVisitData) {
      resetDynamicRoutesForTokenChange()
      await refreshCurrentUserInfo()
    }
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      if (permissionStore.getIsAddRouters) {
        next()
        return
      }
      await getRouter()
      // 开发者可根据实际情况进行修改
      const redirectPath = from.query.redirect || to.path
      const redirect = decodeURIComponent(redirectPath as string)
      const nextData = to.path === redirect ? { path: to.fullPath, replace: true } : { path: redirect }
      permissionStore.setIsAddRouters(true)
      next(nextData)
    }
  } else {

    if (to.path === '/login') {
      next()
    } else {

      if (queryToken) {
        await applyQueryTokenContext(queryToken)
        await refreshCurrentUserInfo()
        next('/')
      } else if (visitDataContext) {
        await refreshCurrentUserInfo()
        next({ path: to.fullPath, replace: true })
      } else {
        next({
          path: '/login'
        })
      }
    }
  }
})
router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  loadDone()
})

// import router from './router'
// import { useAppStoreWithOut } from '@/store/modules/app'
// import { useCache } from '@/hooks/web/useCache'
// import type { RouteRecordRaw } from 'vue-router'
// import { useTitle } from '@/hooks/web/useTitle'
// import { useNProgress } from '@/hooks/web/useNProgress'
// import { usePermissionStoreWithOut } from '@/store/modules/permission'
// import { useDictStoreWithOut } from '@/store/modules/dict'
// import { usePageLoading } from '@/hooks/web/usePageLoading'
// import { getDictApi } from '@/api/common'

// const permissionStore = usePermissionStoreWithOut()

// const appStore = useAppStoreWithOut()

// const dictStore = useDictStoreWithOut()

// const { wsCache } = useCache()

// const { start, done } = useNProgress()

// const { loadStart, loadDone } = usePageLoading()

// const whiteList = ['/login'] // 不重定向白名单

// router.beforeEach(async (to, from, next) => {
//   start()
//   loadStart()
//   if (wsCache.get(appStore.getUserInfo)) {
//     if (to.path === '/login') {
//       next({ path: '/' })
//     } else {
//       if (!dictStore.getIsSetDict) {
//         // 获取所有字典
//         const res = await getDictApi()
//         if (res) {
//           dictStore.setDictObj(res.data)
//           dictStore.setIsSetDict(true)
//         }
//       }
//       if (permissionStore.getIsAddRouters) {
//         next()
//         return
//       }

//       // 开发者可根据实际情况进行修改
//       const roleRouters = wsCache.get('roleRouters') || []
//       const userInfo = wsCache.get(appStore.getUserInfo)

//       // 是否使用动态路由
//       if (appStore.getDynamicRouter) {
//         userInfo.role === 'admin'
//           ? await permissionStore.generateRoutes('admin', roleRouters as AppCustomRouteRecordRaw[])
//           : await permissionStore.generateRoutes('test', roleRouters as string[])
//       } else {
//         await permissionStore.generateRoutes('none')
//       }

//       permissionStore.getAddRouters.forEach((route) => {
//         router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
//       })
//       const redirectPath = from.query.redirect || to.path
//       const redirect = decodeURIComponent(redirectPath as string)
//       const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
//       permissionStore.setIsAddRouters(true)
//       next(nextData)
//     }
//   } else {
//     if (whiteList.indexOf(to.path) !== -1) {
//       next()
//     } else {
//       next(`/login?redirect=${to.path}`) // 否则全部重定向到登录页
//     }
//   }
// })

// router.afterEach((to) => {
//   useTitle(to?.meta?.title as string)
//   done() // 结束Progress
//   loadDone()
// })
